import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { Institution } from './institution.js';
import type { User } from './user.js';

@Entity()
export class LecturerContractFile extends AdornisFile {
  static override _class = 'LecturerContractFile';
}

export enum LecturerContractVariable {
  Vorname = 'Vorname',
  Nachname = 'Nachname',
  Anschrift = 'Anschrift',
  Produkt_Titel = 'Produkt_Titel',
  Produkt_Tag = 'Produkt_Tag', // Wochentag
  Produkt_Datum = 'Produkt_Datum',
  Produkt_Uhrzeit = 'Produkt_Uhrzeit',
  Semester = 'Semester',
  Datum = 'Datum',
  Verguetung_Euro = 'Verguetung_Euro',
  Verguetung_Ausgeschrieben = 'Verguetung_Ausgeschrieben',
}

/**
 * @entity LecturerContract
 */
@Entity()
export class LecturerContract extends MongoEntity {
  static override _class = 'LecturerContract';
  static override _collectionName = 'lecturer-contracts';

  @Field(type => String)
  @validate(nonOptional())
  title!: string;

  @Field(type => Date, { default: v => v ?? new Date() })
  createdAt!: Date;

  @Field(type => String)
  @validate(nonOptional())
  text!: string;

  @Field(type => String)
  fileID!: string;

  @Field(type => LecturerContractFile, {
    resolve: function (this: LecturerContract) {
      return async gqlFields => loadMongoData(LecturerContractFile, this.fileID);
    },
  })
  file: Maybe<LecturerContractFile>;

  @validate(nonOptional())
  @Field(type => String)
  public institutionID!: string;

  static async getAllowedLecturerContracts() {
    // check permissions
    const userID = context.serverContext ? undefined : (context.userID as string | undefined);
    if (!userID) return [];
    const user = context.serverContext
      ? undefined
      : await CurrentUserInfo.getMyself<User>()({ roles: AdornisRoleToContextsWrapper.allFields });
    if (!user) return [];

    const institutionIDs = await Institution.getAllowedInstitutions(true);

    const result = await (
      await getRawCollection(LecturerContract._collectionName)
    )
      .aggregate<{ _id: string }>([
        {
          $match: {
            institutionID: {
              $in: institutionIDs,
            },
          },
        },
        {
          $project: {
            _id: 1,
          },
        },
      ])
      .toArray();

    return result.map(res => res._id);
  }
}
